/**
 * LightPosts — postes de luz nas esquinas das quadras. Cada poste tem um globo
 * emissivo (captado pelo bloom) e uma poça de luz suave no chão, recriando a
 * iluminação pública da referência (cidade noturna).
 */
import * as THREE from 'three';
import { COLORS } from '../config.js';
import { BLOCKS, DISTRICTS, districtOfBlock } from './cityLayout.js';

// A cidade tinge a luz por bairro? (só então a poça precisa de textura neutra)
const TINTED = DISTRICTS.some((d) => d.lamp);

export function createLightPosts() {
  const group = new THREE.Group();
  group.name = 'lightPosts';

  const metal = new THREE.MeshStandardMaterial({
    color: '#26302a', roughness: 0.6, metalness: 0.5
  });
  const glow = new THREE.MeshBasicMaterial({ color: '#fff3cf' }); // estoura no bloom
  // LUZ POR DISTRITO (só onde a cidade tem `districts` com cor): o globo sai no tom
  // claro derivado da cor do bairro (cityLayout.DISTRICTS.lamp). Como o globo é
  // emissivo e pega o bloom, é ele que leva a cor do distrito para a noite. Cidade sem
  // distrito não passa por aqui: mesmo material de sempre, 1 bucket no batchStatic.
  const glowByDistrict = new Map();
  const glowFor = (dist) => {
    if (!dist || !dist.lamp) return glow;
    if (!glowByDistrict.has(dist.id)) {
      glowByDistrict.set(dist.id, new THREE.MeshBasicMaterial({ color: dist.lamp }));
    }
    return glowByDistrict.get(dist.id);
  };
  // Poça de luz: gradiente radial (claro no centro, some nas bordas) com
  // blending ADITIVO → parece claridade suave no chão, não um disco/esfera.
  // Com luz por bairro a textura vai NEUTRA e a cor entra pelo `instanceColor`: a
  // textura quente original multiplicaria o azul/roxo e devolveria um tom sujo. Sem
  // distrito colorido segue a textura quente de sempre — nada muda nas outras cidades.
  const poolMat = new THREE.MeshBasicMaterial({
    map: makeGlowTexture(TINTED),
    transparent: true,
    opacity: 0.5,
    depthWrite: false,
    blending: THREE.AdditiveBlending
  });

  // geometrias compartilhadas
  const baseGeo = new THREE.CylinderGeometry(0.3, 0.42, 0.5, 8);
  const poleGeo = new THREE.CylinderGeometry(0.11, 0.15, 5.4, 8);
  const armGeo = new THREE.CylinderGeometry(0.45, 0.34, 0.5, 12);
  const globeGeo = new THREE.SphereGeometry(0.38, 12, 12);
  // poça: plano deitado já na geometria (a instância só posiciona/translada)
  const poolGeo = new THREE.PlaneGeometry(9, 9).rotateX(-Math.PI / 2);

  // PARTE SÓLIDA do poste (base/mastro/cap/globo): meshes normais — o batchStatic
  // funde tudo em ~2 chamadas (1 por material) na montagem da cena.
  const makePost = (glowMat) => {
    const g = new THREE.Group();
    const base = new THREE.Mesh(baseGeo, metal);
    base.position.y = 0.25;
    base.castShadow = true;
    const pole = new THREE.Mesh(poleGeo, metal);
    pole.position.y = 2.95;
    pole.castShadow = true;
    const cap = new THREE.Mesh(armGeo, metal);
    cap.position.y = 5.7;
    const globe = new THREE.Mesh(globeGeo, glowMat);
    globe.position.y = 6.05;
    g.add(base, pole, cap, globe);
    return g;
  };

  // Esquinas de cada quadra, recuadas até quase a junção da calçada com a área
  // verde (bem longe do meio-fio), deduplicadas
  const spots = [];
  const seen = new Set();
  const inset = 5.4;
  for (const block of BLOCKS) {
    // Numa SUPERQUADRA os postes seguem o CANTEIRO (centrado no prédio) em vez da
    // célula da grade — senão ficariam alinhados com as ruas que sumiram, tortos em
    // relação ao prédio. Mesmo recuo de sempre, só que medido a partir do gramado.
    const l = block.bed || block.land;
    const dist = districtOfBlock(block); // o poste fica DENTRO da quadra → herda o bairro
    for (const cx of [l.left, l.right]) {
      for (const cz of [l.top, l.bottom]) {
        const x = cx + (cx === l.left ? inset : -inset);
        const z = cz + (cz === l.top ? inset : -inset);
        const key = `${Math.round(x)},${Math.round(z)}`;
        if (seen.has(key)) continue;
        seen.add(key);
        spots.push([x, z, dist]);
        const post = makePost(glowFor(dist));
        post.position.set(x, 0, z);
        group.add(post);
      }
    }
  }

  // POÇAS DE LUZ num único InstancedMesh. Antes eram N planos independentes e,
  // por serem TRANSPARENTES, o batchStatic não podia fundi-los → 1 draw call cada
  // (medido: 56). Como o blending é ADITIVO, o resultado NÃO depende da ordem de
  // desenho: juntar tudo numa instância só dá exatamente a mesma imagem.
  const pools = new THREE.InstancedMesh(poolGeo, poolMat, spots.length);
  pools.name = 'lightPools';
  pools.castShadow = false;
  pools.receiveShadow = false;
  const m = new THREE.Matrix4();
  for (let i = 0; i < spots.length; i++) {
    m.makeTranslation(spots[i][0], 0.06, spots[i][1]);
    pools.setMatrixAt(i, m);
  }
  pools.instanceMatrix.needsUpdate = true;
  // POÇA de luz no tom do bairro, POR INSTÂNCIA (`instanceColor` multiplica a textura)
  // — mantém 1 draw call só. Sem distrito com cor o buffer nem é criado: idêntico.
  if (spots.some((s) => s[2] && s[2].lamp)) {
    const c = new THREE.Color();
    for (let i = 0; i < spots.length; i++) {
      const d = spots[i][2];
      pools.setColorAt(i, c.set(d && d.lamp ? d.lamp : '#ffffff'));
    }
    pools.instanceColor.needsUpdate = true;
  }
  group.add(pools);

  return group;
}

/** Textura de brilho radial (centro → transparente) para a poça de luz. `neutral`
 *  troca o quente por branco, para a cor do bairro entrar limpa pelo instanceColor. */
function makeGlowTexture(neutral) {
  const s = 128;
  const cv = document.createElement('canvas');
  cv.width = cv.height = s;
  const c = cv.getContext('2d');
  const g = c.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
  const rgb = neutral ? ['255,255,255', '255,255,255', '255,255,255'] : ['255,238,196', '255,232,180', '255,230,175'];
  g.addColorStop(0, `rgba(${rgb[0]},0.55)`);
  g.addColorStop(0.35, `rgba(${rgb[1]},0.2)`);
  g.addColorStop(0.7, `rgba(${rgb[2]},0.05)`);
  g.addColorStop(1, `rgba(${rgb[2]},0)`);
  c.fillStyle = g;
  c.fillRect(0, 0, s, s);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}
